/**
 * 菜单服务模块
 * 提供菜单生成、过滤等统一的业务逻辑
 */

import {
  MenuItem,
  DEFAULT_HOME_MENU,
  ENTERPRISE_MENU_CONFIG,
  MemberGrade,
} from './menuConfig';
import { rightJudgment, judgementShwo } from '@/utils/utils';

/**
 * 判断当前用户信息是否存在非空的 companyStudioDomainName 字段
 * @returns 是否拥有创作工作室域名
 */
export function hasStudioDomain(): boolean {
  try {
    const userInfo = JSON.parse(localStorage.getItem('user') || '{}');
    return !!(userInfo && userInfo.companyStudioDomainName);
  } catch (error) {
    return false;
  }
}

/**
 * 菜单生成服务类
 */
export class MenuService {
  /**
   * 生成主页导航菜单列表
   * @param memberGrade - 会员等级
   * @param authorizedRoute - 是否启用权限路由
   * @returns 过滤后的菜单列表
   */
  static generateHomeMenuList(
    memberGrade: string,
    authorizedRoute: boolean
  ): MenuItem[] {
    let baseMenu: MenuItem[];

    // 非企业版用户或未启用权限路由，使用默认菜单
    if (memberGrade !== MemberGrade.ENTERPRISE || !authorizedRoute) {
      baseMenu = [...DEFAULT_HOME_MENU];
    } else {
      // 企业版且启用权限路由，根据权限过滤菜单
      baseMenu = this.filterMenuByPermission(ENTERPRISE_MENU_CONFIG);
    }

    // 过滤掉需要 companyStudioDomainName 但当前用户不满足条件的菜单
    return this.filterMenuByStudioDomain(baseMenu);
  }

  /**
   * 过滤需要 companyStudioDomainName 的菜单
   * Non-OEM (official domain) always keeps these menus; OEM only keeps them
   * when the user has a non-empty companyStudioDomainName.
   * @param menuList - 待过滤的菜单列表
   * @returns 过滤后的菜单列表
   */
  static filterMenuByStudioDomain(menuList: MenuItem[]): MenuItem[] {
    if (judgementShwo() || hasStudioDomain()) {
      return menuList;
    }
    return menuList.filter((item) => !item.requiresStudioDomain);
  }

  /**
   * 根据权限过滤菜单
   * @param menuList - 待过滤的菜单列表
   * @returns 过滤后的菜单列表
   */
  static filterMenuByPermission(menuList: MenuItem[]): MenuItem[] {
    return menuList.filter(
      (item) => !item.permission || rightJudgment(item.permission)
    );
  }

  /**
   * 根据路由名称查找菜单项
   * @param menuList - 菜单列表
   * @param routerName - 路由名称
   * @returns 找到的菜单项，未找到返回 undefined
   */
  static findMenuByRouterName(
    menuList: MenuItem[],
    routerName: string
  ): MenuItem | undefined {
    return menuList.find((item) => item.routerName === routerName);
  }

  /**
   * 检查菜单项是否有权限
   * @param menuItem - 菜单项
   * @returns 是否有权限
   */
  static hasPermission(menuItem: MenuItem): boolean {
    if (!menuItem.permission) {
      return true; // 无权限要求，默认有权限
    }
    return rightJudgment(menuItem.permission);
  }

  /**
   * 获取默认菜单（用于初始化）
   * @returns 默认菜单列表
   */
  static getDefaultMenu(): MenuItem[] {
    return [...DEFAULT_HOME_MENU];
  }

  /**
   * 获取企业版菜单配置（用于预览等场景）
   * @returns 企业版菜单配置
   */
  static getEnterpriseMenu(): MenuItem[] {
    return [...ENTERPRISE_MENU_CONFIG];
  }
}

/**
 * 生成主页导航菜单列表（兼容旧代码的函数导出）
 * @param memberGrade - 会员等级
 * @param authorizedRoute - 是否启用权限路由
 * @returns 过滤后的菜单列表
 */
export function generateHomeMenuList(
  memberGrade: string,
  authorizedRoute: boolean
): MenuItem[] {
  return MenuService.generateHomeMenuList(memberGrade, authorizedRoute);
}

